import { getToken, setToken, removeToken } from '../utils/tokenStorage';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ChatBox from '../components/ChatBox';
import { CreateGroupModal, ExpressInterestModal } from '../components/PropertyModals';
import PropertyGroupsSection from '../components/PropertyGroupsSection';
import PropertyDetailsSections from '../components/PropertyDetailsSections';
import { cloudinaryOptimize } from '../utils/cloudinaryOptimize';
import './PropertyDetailPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDetailPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [interestForm, setInterestForm] = useState({ message: '', phone: '' });
  const [interestLoading, setInterestLoading] = useState(false);
  const [interestSent, setInterestSent] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [createGroupForm, setCreateGroupForm] = useState({ group_type: 'Mixed', is_couple: false });
  const [openGroupChats, setOpenGroupChats] = useState({});

  const getAuthConfig = useCallback(() => {
    const token = getToken();
    return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
  }, []);

  const checkFavoriteStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/favorites?item_type=property`, getAuthConfig());
      const fav = response.data.find(f => f.item_id === propertyId);
      if (fav) { setIsFavorited(true); setFavoriteId(fav.favorite_id); }
    } catch (e) { console.error("Property page error:", e); }
  }, [propertyId, getAuthConfig]);

  const checkInterestStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/interests/my`, getAuthConfig());
      if (response.data.some(i => i.property_id === propertyId)) setInterestSent(true);
    } catch (e) { console.error("Property page error:", e); }
  }, [propertyId, getAuthConfig]);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, getAuthConfig());
      setCurrentUser(response.data);
      checkFavoriteStatus();
      checkInterestStatus();
    } catch (error) {
      // not authenticated
    }
  }, [getAuthConfig, checkFavoriteStatus, checkInterestStatus]);

  const fetchProperty = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/properties/${propertyId}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Failed to fetch property:', error);
      setError('Property not found');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  const fetchPropertyGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups?property_id=${propertyId}`, getAuthConfig());
      setPropertyGroups(response.data);
    } catch (e) { console.error("Property page error:", e); }
  }, [propertyId, getAuthConfig]);

  useEffect(() => {
    fetchProperty();
    fetchCurrentUser();
    fetchPropertyGroups();
  }, [fetchProperty, fetchCurrentUser, fetchPropertyGroups]);

  // SEO: archived listings must be noindex so Google de-indexes them
  // (prevents Soft-404 flags in Search Console).
  useEffect(() => {
    if (!property) return;
    const robots = document.querySelector('meta[name="robots"]');
    if (!robots) return;
    if (property.status === 'archived') {
      robots.setAttribute('content', 'noindex,follow');
    }
    return () => {
      // Restore default when leaving this detail page
      const r = document.querySelector('meta[name="robots"]');
      if (r) r.setAttribute('content', 'index,follow,max-image-preview:large');
    };
  }, [property]);

  const handleExpressInterest = async (e) => {
    e.preventDefault();
    setInterestLoading(true);
    try {
      await axios.post(`${API}/interests`, {
        property_id: propertyId,
        message: interestForm.message,
        phone: interestForm.phone
      }, getAuthConfig());
      setInterestSent(true);
      setShowInterestModal(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to express interest');
    } finally {
      setInterestLoading(false);
    }
  };

  const toggleFavorite = async () => {
    try {
      if (isFavorited && favoriteId) {
        await axios.delete(`${API}/favorites/${favoriteId}`, getAuthConfig());
        setIsFavorited(false);
        setFavoriteId(null);
      } else {
        const response = await axios.post(`${API}/favorites`, {
          item_id: propertyId,
          item_type: 'property'
        }, getAuthConfig());
        setIsFavorited(true);
        setFavoriteId(response.data.favorite?.favorite_id);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const createPropertyGroup = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/groups`, {
        property_id: propertyId,
        group_type: createGroupForm.group_type,
        is_couple: createGroupForm.is_couple
      }, getAuthConfig());
      setShowCreateGroupModal(false);
      setCreateGroupForm({ group_type: 'Mixed', is_couple: false });
      fetchPropertyGroups();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create group');
    }
  };

  const joinPropertyGroup = async (groupId) => {
    try {
      const res = await axios.post(`${API}/groups/${groupId}/join`, {}, getAuthConfig());
      alert(res.data.message);
      fetchPropertyGroups();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to join');
    }
  };

  const leavePropertyGroup = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/leave`, {}, getAuthConfig());
      fetchPropertyGroups();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to leave group');
    }
  };

  const swapToGroup = async (groupId) => {
    try {
      const res = await axios.post(`${API}/groups/${groupId}/swap`, {}, getAuthConfig());
      alert(res.data.message);
      fetchPropertyGroups();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to swap group');
    }
  };

  if (loading) {
    return (
      <div className="property-detail-page">
        <div className="container">
          <div className="loading">Loading property details...</div>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="property-detail-page">
        <div className="container">
          <div className="error-state">
            <h2>Property Not Found</h2>
            <p>The property you're looking for doesn't exist or has been removed.</p>
            <button className="btn btn-primary" onClick={() => navigate('/properties')}>
              Browse Properties
            </button>
          </div>
        </div>
      </div>
    );
  }

  const images = property.images?.length > 0 ? property.images : [];
  const hasImages = images.length > 0;

  return (
    <div className="property-detail-page" data-testid="property-detail-page">
      <div className="detail-header">
        <div className="container">
          <button className="back-btn" onClick={() => navigate('/properties')} data-testid="back-btn">
            &larr; Back to Properties
          </button>
        </div>
      </div>

      <div className="container">
        <div className="property-detail-layout">
          {/* Image Gallery + Details below it */}
          <div className="image-gallery">
            <div className="main-image">
              {hasImages ? (
                <img src={cloudinaryOptimize(images[selectedImage], 1200)} alt={property.address} />
              ) : (
                <div className="placeholder-image-large">
                  <span>No Images Available</span>
                </div>
              )}
            </div>
            {hasImages && images.length > 1 && (
              <div className="thumbnail-row">
                {images.map((img, index) => (
                  <div
                    key={img || index}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img src={cloudinaryOptimize(img, 200)} alt={`View ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
            {/* Contact + description sections sit under the photo in the left column */}
            <PropertyDetailsSections property={property} />
          </div>

          {/* Property Info */}
          <div className="property-info-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div className="property-type-badge">{property.property_type}</div>
              {currentUser && (
                <button
                  onClick={toggleFavorite}
                  style={{
                    background: 'none', border: '2px solid ' + (isFavorited ? '#ef4444' : '#d1d5db'),
                    borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', fontSize: '18px',
                    color: isFavorited ? '#ef4444' : '#9ca3af', transition: 'all 0.2s'
                  }}
                  data-testid="favorite-btn"
                  title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {isFavorited ? '♥' : '♡'} {isFavorited ? 'Saved' : 'Save'}
                </button>
              )}
            </div>
            <h1 className="property-title" data-testid="property-title">
              {property.city} {property.state} {property.postcode}
            </h1>
            {property.address && (
              <p style={{ color: '#6b7280', fontSize: '16px', marginTop: '4px', marginBottom: '8px' }}>{property.address}</p>
            )}

            <div className="price-box">
              {property.weekly_rent_per_person > 0 && property.total_bedrooms > 0 && <div style={{ fontSize: '15px', color: 'rgba(255,255,255,0.85)', fontWeight: '600', marginBottom: '8px' }}>Gross Rent: ${(property.weekly_rent_per_person * property.total_bedrooms).toFixed(0)}/week ({property.total_bedrooms} bedrooms)</div>}
              {property.weekly_rent_per_person > 0 ? (
                <>
                  <div className="price-amount">${property.weekly_rent_per_person}</div>
                  <div className="price-period">weekly rent per bedroom</div>
                </>
              ) : property.total_rent > 0 ? (
                <>
                  <div className="price-amount">${property.total_rent}</div>
                  <div className="price-period">weekly total rent</div>
                </>
              ) : (
                <div className="price-period">Price on application</div>
              )}
              {property.weekly_rent_per_person > 0 && <div style={{ marginTop: '12px', padding: '14px', background: 'rgba(255,255,255,0.15)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.25)' }}>
                <div style={{ fontWeight: '700', color: 'white', marginBottom: '10px', fontSize: '15px' }}>After CRA (Commonwealth Rent Assistance):</div>
                {(() => {
                  const r = property.weekly_rent_per_person;
                  const sCRA = Math.min(73.23, Math.max(0, 0.75 * (r - 74.10)));
                  const cCRA = Math.min(103.40, Math.max(0, 0.75 * (r - 123.10)));
                  const sNet = (r - sCRA).toFixed(2);
                  const cNet = (r - cCRA).toFixed(2);
                  return <>
                    <div style={{ marginBottom: '8px', padding: '8px 10px', background: 'rgba(255,255,255,0.15)', borderRadius: '6px' }}>
                      <div style={{ fontWeight: '600', color: 'white', fontSize: '14px', marginBottom: '4px' }}>Singles:</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                        <span>CRA Subsidy:</span>
                        <span style={{ color: '#86efac', fontWeight: '600' }}>-${sCRA.toFixed(2)}/wk</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', marginTop: '2px' }}>
                        <span style={{ fontWeight: '600', color: 'white' }}>Net Rent:</span>
                        <span style={{ fontWeight: '700', color: '#86efac', fontSize: '18px' }}>${sNet}/wk</span>
                      </div>
                    </div>
                    <div style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.15)', borderRadius: '6px' }}>
                      <div style={{ fontWeight: '600', color: 'white', fontSize: '14px', marginBottom: '4px' }}>Couples (each):</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                        <span>CRA Subsidy:</span>
                        <span style={{ color: '#86efac', fontWeight: '600' }}>-${cCRA.toFixed(2)}/wk</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', marginTop: '2px' }}>
                        <span style={{ fontWeight: '600', color: 'white' }}>Net Rent:</span>
                        <span style={{ fontWeight: '700', color: '#86efac', fontSize: '18px' }}>${cNet}/wk</span>
                      </div>
                    </div>
                  </>;
                })()}
              </div>}
            </div>

            <div className="property-highlights">
              <div className="highlight-item">
                <span className="highlight-icon">Bed</span>
                <div className="highlight-text">
                  <strong>{property.available_bedrooms > 0 ? property.available_bedrooms : property.total_bedrooms || '?'}</strong>
                  <span>bedrooms available</span>
                </div>
              </div>
              <div className="highlight-item">
                <span className="highlight-icon">Home</span>
                <div className="highlight-text">
                  <strong>{property.total_bedrooms || '?'}</strong>
                  <span>total bedrooms</span>
                </div>
              </div>
              <div className="highlight-item">
                <span className="highlight-icon">Bath</span>
                <div className="highlight-text">
                  <strong>{property.total_bathrooms > 0 ? property.total_bathrooms : 'TBA'}</strong>
                  <span>bathrooms</span>
                </div>
              </div>
            </div>

            {currentUser && (
              <div className="property-actions">
                {interestSent ? (
                  <button className="btn btn-secondary btn-large" disabled data-testid="interest-sent-btn">
                    Interest Sent
                  </button>
                ) : (
                  <button
                    className="btn btn-primary btn-large contact-btn"
                    onClick={() => setShowInterestModal(true)}
                    data-testid="express-interest-btn"
                  >
                    Express Interest
                  </button>
                )}
                {(currentUser.user_id === property.added_by_user_id || currentUser.role === 'admin') && (
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => navigate(`/properties/${propertyId}/edit`)}
                    data-testid="edit-property-btn"
                  >
                    Edit Property
                  </button>
                )}
              </div>
            )}
            {!currentUser && (
              <p className="login-prompt">
                <a href="/apply">Apply to become a member</a> to express interest in this property.
              </p>
            )}
          </div>
        </div>
      </div>

      <PropertyGroupsSection
        currentUser={currentUser}
        propertyGroups={propertyGroups}
        openGroupChats={openGroupChats}
        setOpenGroupChats={setOpenGroupChats}
        setShowCreateGroupModal={setShowCreateGroupModal}
        joinPropertyGroup={joinPropertyGroup}
        leavePropertyGroup={leavePropertyGroup}
        swapToGroup={swapToGroup}
      />

      {showCreateGroupModal && (
        <CreateGroupModal
          property={property}
          createGroupForm={createGroupForm}
          setCreateGroupForm={setCreateGroupForm}
          onSubmit={createPropertyGroup}
          onClose={() => setShowCreateGroupModal(false)}
        />
      )}

      {showInterestModal && (
        <ExpressInterestModal
          property={property}
          interestForm={interestForm}
          setInterestForm={setInterestForm}
          onSubmit={handleExpressInterest}
          onClose={() => setShowInterestModal(false)}
          loading={interestLoading}
        />
      )}
    </div>
  );
};

export default PropertyDetailPage;
